import React, { useCallback, useEffect, useRef } from 'react';
import { useTable, useSortBy, useFilters, usePagination } from 'react-table';
import {
    Table,
    TableContainer,
    TableHead as MuiTableHead,
    TableBody as MuiTableBody,
    TableRow as MuiTableRow,
    TableCell,
    TableSortLabel,
    TablePagination,
    LinearProgress,
    makeStyles,
} from '@material-ui/core';
import { useDebounce, useState, useLocalStorage } from 'hooks';
import { formatQuery } from 'modules/api/utils/helpers';
import AnalyticsSelectFilter from './filters/SelectFilter';

export { CellWithTooltip } from '../DataTable/';
export { AnalyticsSelectFilter, AnalyticsRangeFilter } from './filters';

const useStyles = makeStyles(() => ({
    wrapper: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
    },
    table: { tableLayout: 'fixed' },
    th: {
        backgroundColor: '#F0F6F3 !important',
        color: '#3F7C5D !important',
        fontSize: 11,
        fontWeight: 400,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        borderBottom: 'none !important',
        padding: '4px 12px',
        overflow: 'hidden',
        whiteSpace: 'normal',
        '& .MuiTableSortLabel-root': { color: '#3F7C5D !important' },
        '& .MuiTableSortLabel-root:hover': { color: '#0B3F30 !important' },
        '& .MuiTableSortLabel-root.MuiTableSortLabel-active': { color: '#3F7C5D !important' },
        '& .MuiTableSortLabel-icon': { color: '#3F7C5D !important', fontSize: 12 },
    },
    filterTh: {
        backgroundColor: '#F0F6F3 !important',
        height: 30,
        padding: '0 6px !important',
        borderBottom: '2px solid #e0ebe6 !important',
    },
    cell: {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        height: 35,
        padding: '0 12px',
        fontSize: 13,
        color: '#2D2D2D',
    },
    backdrop: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        cursor: 'progress',
        backgroundColor: 'transparent',
    },
    progress: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
    },
}));

function AnalyticsTable(props) {
    const {
        columns: userColumns,
        data: userData,
        loadData: userLoadData,
        pageSizeOptions,
        onFiltersChange,
        footerLeft,
        pageSizeStorageKey,
        tableMinWidth = 1000,
        ...otherProps
    } = props;

    const [data, setData] = useState(userData);
    const [itemCount, setItemCount] = useState(userData.length);
    const [isLoading, setIsLoading] = useState(false);
    const isLoadingDebounced = useDebounce(isLoading, 200);

    const [storedPageSize, setStoredPageSize] = useLocalStorage(
        pageSizeStorageKey || 'analyticsPageSize',
        pageSizeOptions[0] || 10,
    );
    const [currentPageSize, setCurrentPageSize] = useState(storedPageSize);

    const tableInstance = useTable(
        {
            initialState: { pageIndex: 0, pageSize: storedPageSize },
            columns: userColumns,
            data,
            pageCount: Math.ceil(itemCount / currentPageSize),
            ...otherProps,
        },
        useFilters,
        useSortBy,
        usePagination,
    );

    const {
        state: { sortBy, filters, pageIndex, pageSize },
        columns,
        gotoPage,
        setPageSize,
        page: rows,
        prepareRow,
        getTableProps,
        headerGroups,
    } = tableInstance;

    const onPageChange = useCallback((e, p) => gotoPage(p), [gotoPage]);
    const onPageSizeChange = useCallback(
        (e) => {
            const s = +e.target.value;
            setPageSize(s);
            setStoredPageSize(s);
            setCurrentPageSize(s);
        },
        [setPageSize, setStoredPageSize],
    );

    const loadIdRef = useRef(0);
    const loadData = useCallback(() => {
        const id = ++loadIdRef.current;
        setIsLoading(true);

        const query = formatQuery({ page: pageIndex, pageSize, filters, sortBy });

        userLoadData(query, filters)
            .then(({ data: responseData, itemCount: responseCount }) => {
                if (loadIdRef.current !== id) return;

                if (Array.isArray(responseData)) {
                    setData(responseData);
                    setItemCount(responseCount);
                }
                setIsLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
            });
    }, [pageIndex, pageSize, sortBy, filters, userLoadData]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        onFiltersChange?.(filters);
    }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

    const classes = useStyles();
    const visibleCount = columns.filter((c) => c.isVisible).length;

    return (
        <div className={classes.wrapper}>
            <TableContainer style={{ flex: 1, overflow: 'auto' }}>
                <Table {...getTableProps({ className: classes.table, style: { minWidth: tableMinWidth } })}>
                    <MuiTableHead>
                        {headerGroups.map((hg) => (
                            <MuiTableRow {...hg.getHeaderGroupProps()} key={`header-${hg.id}`}>
                                {hg.headers.map((col) => (
                                    <TableCell
                                        key={col.id}
                                        className={classes.th}
                                        style={{ width: col.width, minWidth: col.minWidth }}
                                    >
                                        {col.canSort ? (
                                            <TableSortLabel
                                                {...col.getSortByToggleProps({
                                                    active: col.isSorted,
                                                    direction: col.isSortedDesc ? 'desc' : 'asc',
                                                })}
                                                hideSortIcon={!col.isSorted}
                                            >
                                                {col.render('Header')}
                                                {sortBy.length > 1 && col.isSorted && (
                                                    <span style={{
                                                        fontSize: 9,
                                                        fontWeight: 700,
                                                        lineHeight: 1,
                                                        marginLeft: 2,
                                                        alignSelf: 'flex-start',
                                                        color: '#3F7C5D',
                                                    }}>
                                                        {col.sortedIndex + 1}
                                                    </span>
                                                )}
                                            </TableSortLabel>
                                        ) : (
                                            col.render('Header')
                                        )}
                                    </TableCell>
                                ))}
                            </MuiTableRow>
                        ))}
                        {headerGroups.map((hg) => (
                            <MuiTableRow {...hg.getHeaderGroupProps()} key={`filter-${hg.id}`}>
                                {hg.headers.map((col) => (
                                    <TableCell key={col.id} className={classes.filterTh}>
                                        {col.canFilter ? col.render('Filter') : null}
                                    </TableCell>
                                ))}
                            </MuiTableRow>
                        ))}
                    </MuiTableHead>
                    <MuiTableBody>
                        {rows.length > 0 ? (
                            rows.map((row) => {
                                prepareRow(row);
                                return (
                                    <MuiTableRow
                                        {...row.getRowProps()}
                                        hover
                                        style={
                                            row.index % 2 !== 0
                                                ? { backgroundColor: '#F0F6F3' }
                                                : undefined
                                        }
                                    >
                                        {row.cells.map((cell) => (
                                            <TableCell
                                                key={cell.column.id}
                                                className={classes.cell}
                                                align={cell.column.align}
                                            >
                                                {cell.render('Cell')}
                                            </TableCell>
                                        ))}
                                    </MuiTableRow>
                                );
                            })
                        ) : (
                            <MuiTableRow>
                                <TableCell
                                    colSpan={visibleCount}
                                    align="center"
                                >
                                    {isLoading ? 'Vykdoma užklausa..' : 'Įrašų nerasta'}
                                </TableCell>
                            </MuiTableRow>
                        )}
                    </MuiTableBody>
                </Table>
            </TableContainer>

            <div style={{ display: 'flex', alignItems: 'center' }}>
                {footerLeft && <div style={{ paddingLeft: 8 }}>{footerLeft}</div>}
                <TablePagination
                    style={{ flex: 1 }}
                    labelRowsPerPage="Eilučių puslapyje:"
                    labelDisplayedRows={({ from, to, count }) => `Rodoma: ${from}-${to} iš ${count}`}
                    rowsPerPageOptions={pageSizeOptions}
                    component="div"
                    count={itemCount}
                    rowsPerPage={pageSize}
                    page={pageIndex}
                    backIconButtonProps={{ 'aria-label': 'Ankstesnis puslapis' }}
                    nextIconButtonProps={{ 'aria-label': 'Kitas puslapis' }}
                    onChangePage={onPageChange}
                    onChangeRowsPerPage={onPageSizeChange}
                />
            </div>

            {isLoading && <div className={classes.backdrop} title="Kraunama..." />}
            {isLoadingDebounced && <LinearProgress className={classes.progress} variant="query" />}
        </div>
    );
}

AnalyticsTable.defaultProps = {
    getRowID: (row) => row.id,
    data: [],
    pageSizeOptions: [10, 20, 30, 40, 50],
    defaultColumn: {
        Filter: AnalyticsSelectFilter,
        width: undefined,
    },
    manualPagination: true,
    manualFilters: true,
    manualSortBy: true,
    manualGroupBy: true,
    disableGroupBy: true,
};

export default AnalyticsTable;
